import { TouchLock } from '../index';

import type { useLoggerContext } from './loggerContext';
import type { useTimelineLogger } from './useTimelineLogger';
import type { useInWorkoutTargets } from './useInWorkoutTargets';
import type { useEffortCommit } from './useEffortCommit';
import type { usePlanCues } from './usePlanCues';
import { FocusPanel } from './FocusPanel';
import { FocusPanelTargetStrip } from './FocusPanelTargetStrip';
import { EmptyFocusCard } from './EmptyFocusCard';
import { RosterStrip } from './RosterStrip';
import { buildTimerStartHandlers } from './timerStartHandlers';

type Ctx = ReturnType<typeof useLoggerContext>;
type TimelineLogger = ReturnType<typeof useTimelineLogger>;

// The top of the timeline logger: the roster strip and the FocusPanel — i.e.
// the "pick an exercise" + "the single data-entry point" regions of the three
// the screen doc-comment describes. Split out of TimelineLoggerScreen when the
// shell sat at exactly 500 lines with zero margin (CN-27), following the mould
// TimelineFeedSection already set for the bottom region: a pure render shell
// that reads the same context bag the screen does, owns no state, and receives
// the already-derived values (targets, effort bag, plan cues) as data.
//
// ⚠️ `restFrozen` and `restFreezesNavigation` are two DIFFERENT booleans and
// both arrive here: the first freezes the editing surface (FocusPanel), the
// second the navigation surfaces (the roster's TouchLock) and is false on a
// last-set rest (TM-12). They are passed in — never re-derived from `rest` —
// so the screen keeps computing each exactly once.
export function TimelineFocusSection({
  ctx,
  tl,
  targetRows,
  effort,
  activeCues,
  allLogged,
  restFrozen,
}: {
  ctx: Ctx;
  tl: TimelineLogger;
  targetRows: ReturnType<typeof useInWorkoutTargets>;
  effort: ReturnType<typeof useEffortCommit>;
  /** The active entry's coach cues, already looked up out of usePlanCues. */
  activeCues: ReturnType<ReturnType<typeof usePlanCues>['get']>;
  /** Every exercise fully logged — drives the empty-slot placeholder copy. */
  allLogged: boolean;
  /** A rest is counting → freeze the EDITING surface. Not the same boolean as
   *  tl.restFreezesNavigation. */
  restFrozen: boolean;
}) {
  const {
    draft,
    dispatch,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
    defaultUnit,
    setPickerOpen,
    setEffortScaleOpen,
    setTipEntryIdx,
    setSwapEntryIdx,
    setGroupPickerEntryIdx,
    setSupersetMenu,
    plate,
    hold,
    timerDefaults,
    mode,
  } = ctx;

  const {
    resolvedActiveId,
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    canRemove,
    effortScale,
    groupMembers,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
    focusRow,
    confirmSplitGroup,
    onRemoveExercise,
    onChangeSet,
    onAddSet,
    onRemoveSet,
    onToggleLoadedSides,
    cueKey,
    rest,
    restFreezesNavigation,
    flashCue,
  } = tl;

  return draft.entries.length === 0 ? (
    // Blank ad-hoc workout: empty card explains how to begin (Option B).
    <EmptyFocusCard
      message={t('mobile.logger.empty')}
      addLabel={t('mobile.logger.addExercise')}
      onAddExercise={() => setPickerOpen(true)}
      theme={theme}
    />
  ) : (
    <>
      {/* Roster hybrid (slice 4) — switch which exercise FocusPanel
          targets; each chip shows state + logged/planned count + group
          marker so the queue reads at a glance (v2.67/v2.68 smoke).
          Auto-expanded at start, collapses on pick (slice C). */}
      <TouchLock locked={restFreezesNavigation}>
        <RosterStrip
          entries={draft.entries}
          activeEntryId={resolvedActiveId}
          onFocus={focusRow}
          resolveName={getExerciseNameBySlug}
          groupLabelFor={groupLabelFor}
          expanded={rosterExpanded}
          onToggleExpanded={() => setRosterExpanded((v) => !v)}
          t={t}
          theme={theme}
        />
      </TouchLock>

      {/* The group line is now the FIRST row INSIDE the FocusPanel card
          (Antenore 2026-06-01: "raggruppa assieme la card superset e la
          card esercizio"), so it's no longer rendered separately here. */}
      {activeEntry ? (
        <FocusPanel
          title={getExerciseNameBySlug(
            activeEntry.exerciseId,
            activeEntry.exerciseName,
          )}
          entry={activeEntry}
          set={boundSet}
          activeSetIdx={boundSetIdx >= 0 ? boundSetIdx : null}
          planPerSetNotes={activeCues?.perSetNotes}
          planEntryNote={activeCues?.instruction}
          canRemove={canRemove}
          exercise={activeExercise}
          defaultUnit={defaultUnit}
          effortScale={effortScale}
          onChange={onChangeSet}
          onSelectSet={setSelectedSetIdx}
          onAddSet={onAddSet}
          onRemoveSet={onRemoveSet}
          onToggleUnit={() =>
            dispatch({
              type: 'toggle_unit',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
            })
          }
          onToggleLoadedSides={onToggleLoadedSides}
          onToggleAmrap={() => {
            if (boundSetIdx < 0 || !boundSet) return;
            dispatch({
              type: 'update_set',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
              // OUTCOME only. `amrapPrescribed` is not in the
              // update_set patch key union at all, so no UI path can
              // rewrite the plan's request from here.
              patch: { amrap: !boundSet.amrap },
            });
          }}
          effortMeter={{
            value: effort.value,
            failed: effort.failed,
            onSelect: effort.onSelect,
            onFail: effort.onFail,
            flashRir: effort.flashRir,
            flashKey: effort.flashKey,
          }}
          // Start-timer affordance for any duration-logged entry —
          // cardio + timed holds (TM-5: cardio had none). Gating +
          // countdown-seed rule live in buildTimerStartHandlers.
          timer={buildTimerStartHandlers({
            entry: activeEntry,
            entryIdx: activeEntryIdx,
            boundSet,
            boundSetIdx,
            hold,
            onOpenDefaults: timerDefaults.open,
          })}
          // Edit-scope (slice 5): outside live logging the structural
          // controls (＋ add set, create/split superset, add chain step)
          // are greyed + balloon-locked; allowed edits (values, remove,
          // fail-toggle, replace/remove exercise) stay live. This is the
          // single place the mode becomes `editLocked` — the ~30 reads
          // downstream are prop-drilling, not re-derivations.
          editLocked={mode !== 'log'}
          onOpenPlates={() => plate.openForSet(activeEntryIdx, boundSetIdx)}
          onOpenEffortScale={() => setEffortScaleOpen(true)}
          onSwap={() => setSwapEntryIdx(activeEntryIdx)}
          onSplitGroup={
            activeEntry.group
              ? () => confirmSplitGroup(activeEntry.group!.id)
              : undefined
          }
          historyOpen={activeEntry.historyOpen}
          onToggleHistory={() =>
            dispatch({
              type: 'toggle_entry_history',
              entryIdx: activeEntryIdx,
              next: !activeEntry.historyOpen,
            })
          }
          onOpenTip={() => setTipEntryIdx(activeEntryIdx)}
          onRemoveExercise={onRemoveExercise}
          // Group line (first row of the card): standalone → ＋ Superset
          // (opens the picker); grouped → ‹ › A 0/3 … ✕. Create offered
          // only when there's another exercise to pair with.
          groupMembers={groupMembers}
          groupLabel={
            activeEntry.group ? groupLabelFor(activeEntry.group) : ''
          }
          activeEntryId={resolvedActiveId}
          resolveName={getExerciseNameBySlug}
          onSelectMember={focusRow}
          onCreateGroup={
            // ＋ Superset ALWAYS opens the picker on the SUPERSET tab with
            // THIS exercise auto-included (AP-0k2, Antenore: it must go
            // straight to the superset flow — current exercise already in
            // — not the plain Add-exercise flow, even with one exercise;
            // the superset tab's catalog list supplies the partner).
            activeEntry.group === null
              ? () => setGroupPickerEntryIdx(activeEntryIdx)
              : undefined
          }
          cueKey={cueKey}
          rest={rest}
          restFrozen={restFrozen}
          flashCue={flashCue}
          targetStrip={
            <FocusPanelTargetStrip
              rows={targetRows}
              units={defaultUnit}
              locale={i18n.language}
              resolveExerciseName={(slug) => getExerciseNameBySlug(slug, slug)}
              theme={theme}
            />
          }
          onOpenGroupMenu={
            activeEntry.group
              ? () =>
                  setSupersetMenu({
                    groupId: activeEntry.group!.id,
                    entryIdx: activeEntryIdx,
                  })
              : undefined
          }
          confirm={ctx.deps.confirm}
          theme={theme}
        />
      ) : (
        // No exercise focused. Two cases: every set is logged (the
        // workout just completed — point A end-state) → "all logged, add
        // another or save"; otherwise workout start / after a remove →
        // "pick above or add a new one".
        <EmptyFocusCard
          message={
            allLogged
              ? t('mobile.timeline.focusAllLogged', {
                  defaultValue:
                    'All sets logged — add an exercise or save below.',
                })
              : t('mobile.timeline.focusEmptyHasRoster', {
                  defaultValue:
                    'Select an exercise above to start, or add a new one.',
                })
          }
          addLabel={t('mobile.logger.addExercise')}
          onAddExercise={() => setPickerOpen(true)}
          flashOnAppear={allLogged}
          theme={theme}
        />
      )}
    </>
  );
}
